import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { buildResponseValidationReport } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type UserIdentityProviderLinkErrorCode,
  type UserIdentityProviderLinksAdmin,
} from '../types';
import {
  getUserIdentityProviderLinkErrorCode,
  isExpectedUserIdentityProviderLinkError,
} from '../userIdentityProviderLinkError';
import { zodUserIdentityProviderLinksAdminResponseSchema } from '../validations';

import { zodDeleteUserIdentityProviderLinkAdminEndpointSchema } from './validations';

const ERROR_MESSAGE_BY_CODE: Record<
  UserIdentityProviderLinkErrorCode,
  () => string
> = {
  identity_provider_link_not_found: () =>
    i18n.t(
      ($) =>
        $.user.deleteUserIdentityProviderLinkAdmin.error
          .identityProviderLinkNotFound,
      { ns: 'data' },
    ),
  login_not_found: () =>
    i18n.t(
      ($) => $.user.deleteUserIdentityProviderLinkAdmin.error.loginNotFound,
      { ns: 'data' },
    ),
  login_service_read_only: () =>
    i18n.t(
      ($) =>
        $.user.deleteUserIdentityProviderLinkAdmin.error.loginServiceReadOnly,
      { ns: 'data' },
    ),
  login_service_unavailable: () =>
    i18n.t(
      ($) =>
        $.user.deleteUserIdentityProviderLinkAdmin.error
          .loginServiceUnavailable,
      { ns: 'data' },
    ),
};

// An error without a response, such as a network failure, gets no toast.
function resolveErrorMessage(error: unknown) {
  const code = getUserIdentityProviderLinkErrorCode(error);

  if (code) {
    return ERROR_MESSAGE_BY_CODE[code]();
  }

  return error instanceof HTTPError
    ? i18n.t(($) => $.user.deleteUserIdentityProviderLinkAdmin.error.generic, {
        ns: 'data',
      })
    : '';
}

/**
 * Removes the single sign-on link of a user's login to one identity provider as ADMIN.
 *
 * The user's next single sign-on links their current account at that provider again; the
 * login itself and the user's data stay unchanged. Resolves with the links that remain,
 * parsed strictly: a body that does not match throws instead of resolving.
 *
 * @param userId - The ID of the user.
 * @param identityProvider - The alias of the identity provider, e.g. `strabag`.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-User/operation/remove_identity_provider_link_vadmin_users__user_id__identity_provider_links__identity_provider__delete
 */
export const deleteUserIdentityProviderLinkAdmin = async (
  userId: UUID,
  identityProvider: string,
): Promise<UserIdentityProviderLinksAdmin> => {
  let response: unknown;

  try {
    response = await vestigasApi
      .delete(
        ENDPOINT.USER.DELETE_IDENTITY_PROVIDER_LINK_ADMIN(
          userId,
          identityProvider,
        ),
        {
          validate: {
            pathSegments: zodDeleteUserIdentityProviderLinkAdminEndpointSchema,
          },
        },
      )
      .json();
  } catch (error) {
    if (!isExpectedUserIdentityProviderLinkError(error)) {
      log.error({
        message: 'Error removing user identity provider link',
        error,
      });
    }

    const errorMessage = resolveErrorMessage(error);
    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }

  const parsed =
    zodUserIdentityProviderLinksAdminResponseSchema.safeParse(response);

  if (!parsed.success) {
    log.responseValidation({
      ...buildResponseValidationReport(parsed.error.issues),
      key: 'delete-user-identity-provider-link-admin',
      message:
        'delete user identity provider link response has validation errors',
    });

    throw new Error(
      'Delete user identity provider link response did not match the expected shape',
    );
  }

  return parsed.data;
};
